/**
 * One card per role on the "Roles & permissions" tab.
 *
 * Two tiers per card, deliberately unequal in visual weight: "Can do" is a
 * fixed checklist of real actions (present or absent, both shown, so two
 * roles can be compared at a glance); "Can see" is one bundled sentence for
 * lower-stakes column/field visibility -- nobody needs four checkmarks for
 * what's functionally one thing.
 */

import { memo } from 'react';
import { Typography, Space, Tag } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { statusPalette, toneStyle } from '../../../jobs/utils/statusPalette';
import {
  ROLE_DESCRIPTIONS,
  ROLE_TAG_WEIGHT,
  roleCanDo,
  roleSeesLine,
} from '../../utils/roleCapabilities';
import { roleLabelMapping, UserRole } from '../../utils/role';
import styles from './index.module.css';

const { Title, Text } = Typography;

export interface RolePermissionsCardProps {
  role: UserRole;
  /** Super Admin is shown for transparency but is never assignable. */
  assignable: boolean;
}

export const RolePermissionsCard = memo(
  ({ role, assignable }: RolePermissionsCardProps) => {
    const label = roleLabelMapping[role];
    const tone = toneStyle(
      ROLE_TAG_WEIGHT[role] === 'elevated' ? 'active' : 'idle',
    );
    const canDo = roleCanDo(role);
    const seesLine = roleSeesLine(role);

    return (
      <div
        className={
          assignable
            ? styles.card
            : `${styles.card} ${styles.notAssignableCard}`
        }
      >
        <Space
          align="center"
          size={8}
          className={styles.cardHeader}
          wrap={false}
        >
          <Tag
            style={{
              color: tone.color,
              background: tone.background,
              borderColor: tone.borderColor,
            }}
            className={styles.roleTag}
          >
            {label}
          </Tag>
          {!assignable && (
            <Text type="secondary" className={styles.notAssignableLabel}>
              Not assignable
            </Text>
          )}
        </Space>
        <Text type="secondary" className={styles.description}>
          {ROLE_DESCRIPTIONS[role]}
        </Text>

        <Title level={5} className={styles.groupTitle}>
          Can do
        </Title>
        <ul className={styles.canDoList}>
          {canDo.map((item) => (
            <li
              key={item.key}
              className={
                item.granted ? styles.canDoItem : styles.canDoItemMuted
              }
            >
              {item.granted && (
                <CheckOutlined
                  className={styles.checkIcon}
                  style={{ color: statusPalette.positive }}
                  aria-hidden="true"
                />
              )}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>

        {seesLine && (
          <>
            <Title level={5} className={styles.groupTitle}>
              Can see
            </Title>
            <Text type="secondary" className={styles.seesLine}>
              {seesLine}
            </Text>
          </>
        )}
      </div>
    );
  },
);

RolePermissionsCard.displayName = 'RolePermissionsCard';

export default RolePermissionsCard;
